import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom"
import AppBar from "@material-ui/core/AppBar";
import {
    AccountBanner,
    AvatarSelect,
    ErrorScreen,
    HeaderToolbar,
    Loading,
    PaymentMethodCard,
    UserProfile
} from "../../components";
import {TextField} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Toolbar from "@material-ui/core/Toolbar";
import {fetchAccount, setUser} from "../../store/actions";
import {useStore} from "../../store";
import {useAuthUser} from "../../components/auth-provider";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import { SentimentVeryDissatisfied } from "@material-ui/icons";
import {Controller, useForm} from "react-hook-form";
import {UserService} from "../../services";
import {MetricService} from "../../services";
import {useSnackbar} from "notistack";
import * as toast from '../../utils/toast'
import {ReactComponent as VisaLogo} from "../../images/visa-logo.svg";
import {ReactComponent as MasterCardLogo} from "../../images/mastercard-logo.svg";
import Info from "@material-ui/icons/Info"
import AddCircleOutline from "@material-ui/icons/AddCircleOutline"
import {scroller} from 'react-scroll';


const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        marginTop: theme.spacing(2),
        justifyContent: 'center',
        // [theme.breakpoints.up('sm')]: {
        //     marginRight: drawerWidth,
        // },
    },
    link: {
        color: '#047e9f',
        fontWeight: 700,
        fontSize: 14,
        '&:hover': {
            textDecoration: 'underline',
            textDecorationColor: '#047e9f',
        },
    },
    inlineLink: {
        color: '#047e9f',
        fontWeight: 700,
        '&:hover': {
            textDecoration: 'underline',
            textDecorationColor: '#047e9f',
        },
    },
    closeButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.primary.contrastText,
    },
    saveButton: {
        minWidth: 100,
    },
    mono: {
        fontFamily: '"Courier New", Courier, monospace',
        // fontWeight: 700,
        fontSize: 16,
    },
    creditCard: {
        display: 'flex',
        borderWidth: 1,
        borderRadius: 4,
        marginBottom: 16,
        maxWidth: 350,
        minHeight: 143,
    },
    widget: {
        marginTop: 8,
        marginBottom: 12,
        padding: 8,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: 3,
    },
    widgetTitle: {
        color: 'rgba(255,255,255,0.75)',
    },
}));


const AccountPage = ({location}) => {
    const classes = useStyles();
    const {user} = useAuthUser();           // Get logged in user
    const [store, dispatch] = useStore();   // Get the global store

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // If we have a location state with a scrollTo property, scroll to that element
    useEffect(() => {
        const el = (location.state && location.state.scrollTo) || ''
        if (el !== '') {
            scroller.scrollTo(el, {
                duration    : 800,
                delay       : 1200,
                offset      : -70,
                smooth      : 'easeInOutQuart',
                containerId : 'psb',
            });
        }
    },[location])

    // Make sure the account is available in the store
    useEffect(()=> {
        dispatch(fetchAccount(user.accountId, store, dispatch))
    }, [user.accountId, store, dispatch])

    // console.log('store.account',store.account)
    // store.account.type='post-paid'
    // store.account.status = -1
    // store.account.balance = -2500

    return (
        <div className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location="/accounts"/>
            </AppBar>
            <Toolbar/>

            <main className={classes.content}>
                { store.account.isFetching ? <Loading/> : null }
                { store.account.error && <ErrorScreen error={store.account.error}/> }
                { store.account.error === null && !store.account.isFetching ?
                    <>

                        <UserProfile variant="account" user={store.user} animation="transition.expandIn" delay={100}/>

                        {/* Display applicable account banners */}
                        <AccountBanner className="mt-4 mb-0" account={store.account} variant="suspended" animation="transition.expandIn" delay={150}/>
                        <AccountBanner className="mt-4 mb-0" account={store.account} variant="reactivate" animation="transition.expandIn" delay={150} />

                        <PersonalDetailsForm user={store.user} />

                        { store.account.type === 'pre-paid' && store.account.balance < 0 ?
                            <PrepaidBalancePanel balance={store.account.balance} status={store.account.status} />
                            : null
                        }

                        <PaymentMethodPanel accountId={store.account.id} paymentMethod={store.account.paymentMethod} />

                        <PurchasesPanel accountId={store.account.id} />

                        <DangerZonePanel status={store.account.status}/>
                    </>
                    : null
                }
            </main>

        </div>

    );
}

const PersonalDetailsForm = () => {
    const classes = useStyles();
    const {handleSubmit, control, formState:{isSubmitting, isDirty, dirtyFields, errors}} = useForm({ mode: 'onChange' });
    const isValid = Object.keys(errors).length === 0;
    const [store, dispatch] = useStore();                       // Get the global store
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();     // Snackbar Hook for notifications

    // onSubmit is called by ReactHookForm when the form is submitted and valid.
    // It returns a promise that is used by RHF to set the isSubmitting flag.
    const onSubmit = (data) => {

        return new Promise((resolve) => {

        // If the form hasn't been changed, bail out
        if (!isDirty) { return }

        const changes = changeSet(dirtyFields, data);
        UserService.patch(store.user.accountId, store.user.id, changes)
            .then(() => {
                // notify the user
                toast.success("Changes saved", enqueueSnackbar, closeSnackbar);
                // Update the global store
                dispatch(setUser({...store.user, ...changes}))

            })
            .catch(error => {
                // Notify the user we hit an error
                toast.apiError("Your changes weren't saved!", error, enqueueSnackbar);

            })
            .finally(() => {
                resolve();  // resolve the promise so RHF knows we've finished submitting
            })

        })

    }

    return(
        <FuseAnimate animation="transition.expandIn" delay={200}>
            <div id="personal-details" className="my-4">
                <Card>
                    <div className="p-4">
                        <Typography variant="h6" color="secondary">Personal Details</Typography>
                        <form
                            name="personalDetailsForm"
                            noValidate
                            className="flex flex-col justify-center, w-full mt-2"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            {/*
                                Input field names should correspond to the field name in the API call
                            */}
                            <div className="flex flex-col md:flex-row">

                                <div className="flex flex-col md:flex-row w-full">
                                    <div className="w-full md:pr-1">
                                    <Controller
                                        name="firstName"
                                        control={control}
                                        defaultValue={store.user.firstName}
                                        rules={{required: 'First name is required'}}
                                        render={ ({field: {onChange, value}, fieldState:{error} }) => (
                                            <TextField
                                                label="First Name"
                                                variant="outlined"
                                                // type="text"
                                                value={value}
                                                onChange={onChange}
                                                error={!!error}
                                                helperText={ error ? error.message : null }
                                                margin="dense"
                                                required
                                                fullWidth
                                            />
                                        )}
                                    />
                                    </div>

                                    <Controller
                                        name="lastName"
                                        control={control}
                                        defaultValue={store.user.lastName}
                                        rules={{required: 'Last name is required'}}
                                        render={ ({field: {onChange, value}, fieldState:{error} }) => (
                                            <TextField
                                                label="Last Name"
                                                variant="outlined"
                                                type="text"
                                                value={value}
                                                onChange={onChange}
                                                error={!!error}
                                                helperText={ error ? error.message : null }
                                                margin="dense"
                                                required
                                                fullWidth
                                            />
                                        )}
                                    />
                                </div>

                            </div>

                            <div className="flex flex-col md:w-1/2 md:pr-1">
                                <Controller
                                    name="avatarUrl"
                                    control={control}
                                    defaultValue={store.user.avatarUrl || 'avatar-00.jpg'}
                                    render={({field: {onChange, value}, fieldState:{error} }) => (
                                        <AvatarSelect
                                            value={value}
                                            onChange={onChange}
                                            initials={store.user.firstName && store.user.lastName ?
                                                store.user.firstName.charAt(0) + store.user.lastName.charAt(0) : ''}
                                        />
                                    )}
                                />
                            </div>

                            <div className="mt-2">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    aria-label="SAVE PERSONAL DETAILS"
                                    className={classes.saveButton}
                                    disabled={!isValid || isSubmitting || !isDirty}
                                >
                                    {isSubmitting ? 'Saving...' : 'Save'}
                                </Button>
                            </div>

                        </form>
                    </div>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const PrepaidBalancePanel = ({balance=0, status=0}) => {

    return(
        <FuseAnimate animation="transition.expandIn" delay={400}>
            <div id="balance-panel" className="mt-4">
                <Card>
                    <div className="p-4">
                        <Typography variant="h6" color="secondary">Account Balance</Typography>
                        <div className="flex flex-row justify-between items-center mt-2">
                            { balance > 0 ?
                                <div className="text-2xl">${(balance/100).toFixed(2)}</div>
                                :
                                <div className="text-2xl text-red-600">${(balance/100).toFixed(2)}</div>
                            }
                        </div>
                    </div>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const PaymentMethodPanel = ({accountId=0, paymentMethod={ id: '', type: '', brand: '', last4: '', expiry: ''}}) => {
    const classes = useStyles();

    return(
        <FuseAnimate animation="transition.expandIn" delay={500}>
            <div id="payment-panel" className="mt-4">
                <Card>
                    <div className="p-4">
                        <Typography variant="h6" color="secondary">Payment Method</Typography>
                        <div className="mt-2">
                            {paymentMethod.type !== '' ?
                                <PaymentMethodCard
                                    accountId={accountId}
                                    paymentMethod={paymentMethod}
                                />
                                :
                                <div className={classes.creditCard}>
                                    <div className="flex flex-col m-auto">
                                        <div className="flex flex-row justify-center mb-2">
                                            <MasterCardLogo height={30} width={45} className="mr-2"/>
                                            <VisaLogo height={30} width={45}/>
                                        </div>
                                        <Button variant="outlined" size="large" color="primary" component={Link} to="/account/add-card">
                                            <AddCircleOutline/>
                                            <span className="ml-1">Add a Card</span>
                                        </Button>
                                    </div>
                                </div>
                            }
                            <div className="border-t border-dashed"/>
                            <div className="flex flex-row mt-3">
                                <Info fontSize="small" color="primary"/>
                                <div className="ml-2 text-xs">
                                    Your card information is securely stored by our payments
                                    partner <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">
                                    <span className={classes.inlineLink}>Stripe</span>
                                </a>, one of the world's leading online
                                    payment providers. We do not store your card details on our servers.
                                </div>
                            </div>

                        </div>
                    </div>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const PurchasesPanel = ({accountId}) => {
    const classes = useStyles();
    const [state, setState] = useState({
        isFetching : false,
        error      : null,
        count      : 0,
        sum        : 0,
    })

    // Load the metric
    useEffect(() => {
        if (accountId === 0) { return }
        let loaded = true;

        setState({isFetching: true, error: null, count: 0, sum: 0})
        MetricService.getPaymentsLast30Days(accountId)
            .then(metric => {
                if (loaded) {
                    setState({isFetching: false, error: null, count: metric.count, sum: metric.sum})
                }
            })
            .catch(error => {
                if (loaded) {
                    setState({isFetching: false, count:0, sum:0, error})
                }
            })

        return () => {loaded = false}
    },[accountId])

    return (
        <FuseAnimate animation="transition.expandIn" delay={600}>
            <div id="purchases-panel" className="mt-4">
                <Card>
                    { state.isFetching ?
                        <div className="py-24"><Loading/></div>
                        :
                        <div className="p-4 flex flex-col" >
                            <Typography variant="h6" color="secondary">Purchases</Typography>
                            { state.error ? <ApiErrorPanel error={state.error}/> : null }
                            { state.error === null && !state.isFetching ?
                                <div className={classes.widget}>
                                    <div className="flex flex-row justify-around items-end">
                                        <div className="flex flex-col">
                                            <Typography variant="overline" className={classes.widgetTitle}>Last 30 Days</Typography>
                                            <Typography variant="h4">{state.count}</Typography>
                                            <Typography variant="overline" className={classes.widgetTitle}>Purchases</Typography>
                                        </div>
                                        <div className="flex flex-col">
                                            <Typography variant="h4">${(state.sum/100).toFixed(2)}</Typography>
                                            <Typography variant="overline" className={classes.widgetTitle}>Total</Typography>
                                        </div>
                                    </div>
                                </div>
                                : null
                            }
                            <Button component={Link} to="/account/purchases" variant="outlined" color="primary" size="large">View all Purchases</Button>
                        </div>
                    }
                </Card>
            </div>
        </FuseAnimate>
    )
}

const DangerZonePanel = ({status}) => {
    const classes = useStyles();

    return (
        <FuseAnimate animation="transition.expandIn" delay={800}>
            <div id="danger-zone" className="my-4">
                <Card>
                    <div className="p-4 flex flex-col">
                        <Typography variant="h6" color="error" >Close Account</Typography>
                        <div className="mt-2 mb-4">
                            <div>Cancel all services and close this account.</div>
                        </div>
                        <Button
                            component={Link}
                            to="/account/close"
                            className={classes.closeButton}
                            variant="contained"
                            disabled={status < 1}
                        >
                            Close Account
                        </Button>
                    </div>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const changeSet = (dirtyFields, fields) => {
    let set = {};
    Object.keys(dirtyFields).forEach((field) => {
        set[field] = fields[field]
    })
    return set;
}

const ApiErrorPanel = ({error}) => (
    <FuseAnimate animation="transition.expandIn" delay={100}>
        <div className="p-4">
            <div className="flex justify-center text-6xl my-1">
                <SentimentVeryDissatisfied fontSize="inherit" color="error" />
            </div>
            <Typography paragraph>
                Sorry, it looks like there's a problem at our end and we can't load this information.
            </Typography>
            <Typography variant="body2" color="textSecondary">{error.status}/{error.statusText}</Typography>
        </div>
    </FuseAnimate>
)


export default AccountPage;